import * as d3 from 'd3';
import i18next from 'i18next';

/**
 * Generates an area chart with a line overlay inside a specified container.
 *
 * @param {string} container_id - The ID of the HTML container element where the chart will be appended.
 * @param {Array} data - An array of objects representing the data to visualize. 
 *                       Each object should have `display_name` (x-axis value) and `count` (y-axis value) properties.
 * @returns {SVGElement} - The generated SVG element containing the area chart.
 */
export function area_chart(container_id, data) {
    const margin = { top: 25, right: 10, bottom: 35, left: 45 };
    let width = 720; // Adjusted to fit container
    let height = 400; 

    // SVG
    const svg = d3.select(`#${container_id}`)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height]);

    // Axis
    const x = d3.scaleBand()
        .domain(data.map(d => d.display_name))
        .range([margin.left, width - margin.right])
        .padding(0.1);

    const y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.count)])
        .nice()
        .range([height - margin.bottom, margin.top]);

    // Declare the area generator.
    const area = d3.area()
        .x(d => x(d.display_name))
        .y0(y(0))
        .y1(d => y(d.count));

    // Declare the upper line
    const line = d3.line()
        .x(d => x(d.display_name))
        .y(d => y(d.count));

    // Append a path for the line
    svg.append("path")
        .datum(data)
        .attr("class", "line")
        .attr("d", line)
        .style("fill", "none")
        .style("stroke", "rgba(0,131,149,1)")
        .style("stroke-width", 3);

    // Append a path for the area (under the axes).
    svg.append("path")
        .attr("fill", "url(#gradient)")
        .attr("d", area(data));

    // Add the x-axis.
    svg.append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).tickSize(0).tickFormat((d, i) => ""))
        .call(g => g.append("text")
                .attr("fill", "currentColor")
                .attr('x', margin.left + (width - margin.left - margin.right) / 2)
                .attr('y', 30)
                .attr("class", "card-sm-num text-center area_chart_x_title")
                .style("font-size", "15px")
                .text(""));

    // Add the y-axis, remove the domain line, add grid lines and a label.
    svg.append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).ticks(height / 40))
        .call(g => g.select(".domain").remove())
        .call(g => g.selectAll(".tick line").clone()
            .attr("x2", width - margin.left - margin.right)
            .attr("stroke-opacity", 0.1))
        .call(g => g.append("text")
            .attr("x", -margin.left)
            .attr("y", 10)
            .attr("fill", "currentColor")
            .attr("text-anchor", "start")
            .attr("class", "card-sm-num text-center")
            .attr("data-i18n", "label-dataset-num")
            .style("font-size", "12px")
            .text(i18next.t('label-dataset-num'))
        );

    return svg.node();
}

/**
 * Updates an area chart with new data.
 *
 * @param {string} container_id - The ID of the HTML container element where the chart will be appended.
 * @param {Array} array - An array of objects representing the new data to visualize. 
 *                        Each object should have `display_name` (x-axis value) and `count` (y-axis value) properties.
 * @returns {SVGElement} - The updated SVG element containing the area chart.
 */
export function update_area_chart(container_id, array) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return area_chart(container_id, array);
    }
    // Update 
    else {
        svg.remove();
        return area_chart(container_id, array);
    }
}